/**
 * Screen Wake Lock while a hand is in progress. SPEC.md §17.2.
 *
 * A phone that locks mid-hand costs the table more than the battery does. Three
 * rules follow from the spec: the lock is only held while a round is active, the
 * OS releases it whenever the page is hidden so it has to be re-requested on
 * return, and every failure path is silent — an unsupported browser, a denied
 * permission or a low-battery refusal must all leave gameplay untouched.
 */

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: 'release', listener: () => void): void;
}

interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

function wakeLockApi(): WakeLockLike | null {
  if (typeof navigator === 'undefined') return null;
  const api = (navigator as unknown as { wakeLock?: WakeLockLike }).wakeLock;
  return api && typeof api.request === 'function' ? api : null;
}

let sentinel: WakeLockSentinelLike | null = null;
/** What the app wants, independent of what the browser has granted. */
let wanted = false;
let listenerInstalled = false;
let pending: Promise<void> | null = null;

/** True only when a lock is genuinely held right now. */
export function wakeLockHeld(): boolean {
  return sentinel !== null && !sentinel.released;
}

function installVisibilityListener(): void {
  if (listenerInstalled || typeof document === 'undefined') return;
  listenerInstalled = true;
  document.addEventListener('visibilitychange', () => {
    // The lock is dropped by the platform on hide, so returning to the tab has
    // to re-acquire it. §17.2
    if (document.visibilityState === 'visible' && wanted && !wakeLockHeld()) {
      void acquireWakeLock();
    }
  });
}

async function requestLock(): Promise<void> {
  const api = wakeLockApi();
  if (!api) return;
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  try {
    const next = await api.request('screen');
    if (!wanted) {
      // Released while the request was in flight.
      void next.release().catch(() => undefined);
      return;
    }
    sentinel = next;
    next.addEventListener('release', () => {
      if (sentinel === next) sentinel = null;
    });
  } catch {
    // Unsupported, denied, or refused on low battery. Gameplay continues. §17.2
    sentinel = null;
  }
}

/** Hold the screen awake. Safe to call repeatedly. */
export async function acquireWakeLock(): Promise<void> {
  wanted = true;
  installVisibilityListener();
  if (wakeLockHeld()) return;
  // Coalesce concurrent callers so a burst of renders makes one request.
  pending ??= requestLock().finally(() => {
    pending = null;
  });
  return pending;
}

/** Release the screen. Safe to call when no lock is held. */
export async function releaseWakeLock(): Promise<void> {
  wanted = false;
  const held = sentinel;
  sentinel = null;
  if (!held || held.released) return;
  try {
    await held.release();
  } catch {
    /* Already gone. */
  }
}
